import { useState } from 'react';
import * as R from 'ramda';
import * as Dfns from 'date-fns/fp';

import { Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { MdInfoOutline } from 'react-icons/md';

import { PlanRow } from '@blisspointmedia/bpm-types/dist/LinearBuying';
import { Button } from '@Components/Button';
import { DatePicker } from '@Components/DatePicker';
import { Appearance, Design, Placement } from '@Components/designConstants';
import { DATE_FORMAT, DateRange } from '@src/utils/date-utils';
import { getNextWeek } from '../linear-buying-utils';

import './ExtendSelectedModal.scss';

interface ExtendSelectedModalProps {
  onClose: () => void;
  rowsToExtend: Record<string, PlanRow>;
  week: string;
  extendFunction: (rowsToExtend: Record<string, PlanRow>, week: string) => Promise<void>;
}

export const ExtendSelectedModal = ({
  onClose,
  rowsToExtend,
  week,
  extendFunction
}: ExtendSelectedModalProps) => {
  const nextWeek = getNextWeek(week);
  const [dates, setDates] = useState<DateRange>({ start: nextWeek, end: nextWeek });
  const [extending, setExtending] = useState(false);

  const addSelected = async () => {
    setExtending(true);
    // Create array of weeks to loop over.
    const weeks: string[] = [];
    let currentWeek = dates.start;
    while (currentWeek <= dates.end) {
      weeks.push(currentWeek);
      currentWeek = R.pipe(Dfns.parseISO, Dfns.addWeeks(1), Dfns.format(DATE_FORMAT))(currentWeek);
    }

    // For each week, call extendFunction
    await Promise.all(weeks.map(extendWeek => extendFunction(rowsToExtend, extendWeek)));
    setExtending(false);
    onClose();
  };

  return (
    <Modal show onHide={onClose} className='extend-selected-modal'>
      <Modal.Header closeButton>
        <Modal.Title className='modal-title'>
          <div className='title'>Extend Selected Rows</div>
          <OverlayTrigger
            placement={Placement.RIGHT_START}
            overlay={
              <Tooltip id='tooltip'>
                <p>
                  Copy and extend the selected rows into a new date range. The weeks in the date
                  picker are inclusive.
                </p>
              </Tooltip>
            }
          >
            <MdInfoOutline />
          </OverlayTrigger>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <DatePicker
          bordered
          range={dates}
          onChange={newDates => setDates(newDates)}
          isOutsideRange={() => false}
          isDayBlocked={date => date <= week || !R.pipe(Dfns.parseISO, Dfns.isMonday)(date)}
          fullWeeksOnly
        />
      </Modal.Body>
      <Modal.Footer>
        <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={onClose}>
          Cancel
        </Button>
        <Button loading={extending} onClick={addSelected}>
          Add Selected
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
